import { AppCard } from "@/components/ui";
import { cn } from "@/utils";
import { AlertCircle, CheckCircle2, ChevronDown } from "lucide-react";
import { useState } from "react";
import type { TFundingRecommendation } from "./types";

const FUNDING_TYPE_LABELS: Record<string, string> = {
  GRANT_FUNDING: "Grant Funding",
  PRE_SEED_INVESTMENT: "Pre-Seed Investment",
  SEED_INVESTMENT: "Seed Investment",
  SERIES_A_INVESTMENT: "Series A Investment",
  VENTURE_DEBT: "Venture Debt",
  REVENUE_BASED_FINANCING: "Revenue-Based Financing",
};

/** "GRANT_FUNDING" -> "Grant Funding" for types outside the known map */
function formatFundingType(fundingType: string): string {
  const known = FUNDING_TYPE_LABELS[fundingType];
  if (known) return known;
  return fundingType
    .split("_")
    .filter(Boolean)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(" ");
}

type TSuitabilityStyle = {
  badge: string;
  bar: string;
};

/**
 * Map free-form suitability levels to semantic colors. "NOT ..." must be
 * checked before the generic "SUITABLE" match.
 */
function getSuitabilityStyle(level: string | undefined): TSuitabilityStyle {
  const key = (level ?? "").toUpperCase();
  if (key.includes("NOT")) {
    return {
      badge: "border-red-300 bg-red-50 text-red-800 dark:border-red-700 dark:bg-red-950/40 dark:text-red-300",
      bar: "bg-red-500",
    };
  }
  if (key.includes("HIGHLY") || key.includes("STRONG") || key.includes("EXCELLENT")) {
    return {
      badge:
        "border-emerald-300 bg-emerald-50 text-emerald-800 dark:border-emerald-700 dark:bg-emerald-950/40 dark:text-emerald-300",
      bar: "bg-emerald-500",
    };
  }
  if (key.includes("MARGINAL") || key.includes("MODERATE") || key.includes("CONDITIONAL") || key.includes("PARTIAL")) {
    return {
      badge:
        "border-amber-300 bg-amber-50 text-amber-800 dark:border-amber-700 dark:bg-amber-950/40 dark:text-amber-300",
      bar: "bg-amber-500",
    };
  }
  if (key.includes("SUITABLE")) {
    return {
      badge:
        "border-emerald-300 bg-emerald-50 text-emerald-800 dark:border-emerald-700 dark:bg-emerald-950/40 dark:text-emerald-300",
      bar: "bg-emerald-500",
    };
  }
  return { badge: "border-border bg-muted text-muted-foreground", bar: "bg-muted-foreground" };
}

type TFundingRecommendationCardProps = {
  recommendation: TFundingRecommendation;
};

function FundingRecommendationCard({ recommendation }: TFundingRecommendationCardProps) {
  const [expanded, setExpanded] = useState(false);
  const name = formatFundingType(recommendation.funding_type ?? "");
  const style = getSuitabilityStyle(recommendation.suitability_level);
  const keyConcerns = recommendation.key_concerns ?? [];
  const keyStrengths = recommendation.key_strengths ?? [];
  const hasDetails =
    Boolean(recommendation.reasoning?.trim()) || keyConcerns.length > 0 || keyStrengths.length > 0;

  return (
    <AppCard padding="none">
      {/* Header row: always visible, toggles the details */}
      <button
        type="button"
        onClick={hasDetails ? () => setExpanded((prev) => !prev) : undefined}
        aria-expanded={expanded}
        className={cn(
          "flex w-full min-w-0 items-center justify-between gap-2 rounded-t-2xl p-3 text-left",
          !expanded && "rounded-b-2xl",
          hasDetails ? "cursor-pointer transition-colors hover:bg-muted/50" : "cursor-default"
        )}
      >
        <div className="flex min-w-0 flex-1 flex-wrap items-center gap-2">
          <h3 className="min-w-0 break-words text-sm font-semibold text-foreground">{name}</h3>
          {recommendation.suitability_level && (
            <span
              className={cn(
                "inline-flex shrink-0 items-center rounded-full border px-2 py-0.5 text-[11px] font-medium leading-4",
                style.badge
              )}
            >
              {recommendation.suitability_level}
            </span>
          )}
        </div>
        <div className="flex shrink-0 items-center gap-2">
          {recommendation.suitability_score != null && (
            <span className="text-xs tabular-nums text-muted-foreground">
              {recommendation.suitability_score}/100
            </span>
          )}
          {hasDetails && (
            <ChevronDown
              className={cn(
                "h-4 w-4 text-muted-foreground transition-transform duration-200",
                expanded && "rotate-180"
              )}
              aria-hidden="true"
            />
          )}
        </div>
      </button>

      {expanded && hasDetails && (
        <div className="min-w-0 space-y-3 border-t border-border p-3">
          {recommendation.reasoning?.trim() && (
            <div>
              <p className="mb-1 text-xs font-medium text-muted-foreground">Reasoning</p>
              <p className="break-words text-sm leading-relaxed text-foreground/80">{recommendation.reasoning}</p>
            </div>
          )}

          {keyConcerns.length > 0 && (
            <div>
              <p className="mb-1 text-xs font-medium text-muted-foreground">Key concerns</p>
              <ul className="space-y-1">
                {keyConcerns.map((concern, index) => (
                  <li
                    key={index}
                    className="flex items-start gap-2 text-sm leading-relaxed text-foreground/80"
                  >
                    <AlertCircle className="mt-0.5 h-3.5 w-3.5 shrink-0 text-amber-600 dark:text-amber-400" />
                    <span className="min-w-0 break-words">{concern}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {keyStrengths.length > 0 && (
            <div>
              <p className="mb-1 text-xs font-medium text-muted-foreground">Key strengths</p>
              <ul className="space-y-1">
                {keyStrengths.map((strength, index) => (
                  <li
                    key={index}
                    className="flex items-start gap-2 text-sm leading-relaxed text-foreground/80"
                  >
                    <CheckCircle2 className="mt-0.5 h-3.5 w-3.5 shrink-0 text-emerald-600 dark:text-emerald-400" />
                    <span className="min-w-0 break-words">{strength}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}
    </AppCard>
  );
}

type TFundingTypeAnalysisSectionProps = {
  recommendations: TFundingRecommendation[];
};

/**
 * "Funding Type Analysis" content: one expandable card per funding type,
 * most suitable options first.
 */
export function FundingTypeAnalysisSection({ recommendations }: TFundingTypeAnalysisSectionProps) {
  const sorted = [...recommendations].sort(
    (a, b) => (b.suitability_score ?? 0) - (a.suitability_score ?? 0)
  );

  return (
    <div className="space-y-2">
      <p className="text-xs text-muted-foreground">
        How suitable each funding type is for your company right now. Click a funding type to see the
        reasoning and key concerns.
      </p>
      {sorted.map((recommendation, index) => (
        <FundingRecommendationCard
          key={recommendation.funding_type ?? index}
          recommendation={recommendation}
        />
      ))}
    </div>
  );
}
